import { useEffect } from 'react'
import { create } from 'zustand'
import { supabase } from '../supabaseClient'
import type { TablesUpdate } from '../types/supabase'
import { getUserId } from '../lib/workos'
import {
  DEFAULT_LEAD_MINUTES,
  isValidLeadMinutes,
  noticeLeadMinutes,
  parseDeadlinePreference,
  type DeadlinePreference,
} from '../utilities/deadlineNotice'

/**
 * The user's compute time limit email preference (nobgp/app#354, router#1552).
 *
 * ⚠⚠ ITS OWN READ, NEVER A COLUMN IN `useProfile`'S SELECT. The two columns come from router
 * migration 0199. Until it is applied, a select that names them fails with 42703, and it fails
 * the WHOLE query: in the profile select that would break the profile for every user. Here the
 * failure only makes the preference unavailable, and the settings block hides itself.
 *
 * ⚠ Not in realtime: a change from another tab is read the next time the settings block opens.
 * Loaded on demand and reset on sign-out (`controller.signOut`).
 */

/**
 * `unknown` — not read yet. `available` — the columns exist and the read worked.
 * `unavailable` — the COLUMNS ARE NOT THERE (42703). Before 0199 is applied this is the normal
 * state, and the settings block renders nothing, because a switch there could only fail.
 * `error` — the columns exist and the read did not work: a network blip, or no profile row.
 *
 * ⚠⚠ THE TWO FAILURES ARE NOT ONE. "The setting does not exist" is the wrong answer to a dropped
 * request: the read runs once per mount and never retries without `force`, so a single blip used
 * to delete the setting from the page for the rest of the visit. 0199 is applied, so `unavailable`
 * is now the unexpected one.
 */
export type PreferenceAvailability = 'unknown' | 'loading' | 'available' | 'unavailable' | 'error'

/** PostgREST's code for a column that does not exist. */
const UNDEFINED_COLUMN = '42703'

export interface DeadlineNotifyState extends DeadlinePreference {
  availability: PreferenceAvailability
  saving: boolean
  /** The result of the last save, for the settings block. Null before a save. */
  saveResult: 'saved' | 'failed' | null
  /** Read the preference. `force` reads again even when it is already known. */
  get: (options?: { force?: boolean }) => Promise<void>
  /** Write one or both fields. Resolves true when the database accepted the write. */
  save: (updates: Partial<DeadlinePreference>) => Promise<boolean>
  reset: () => void
}

const state = {
  availability: 'unknown' as PreferenceAvailability,
  enabled: false,
  leadMinutes: DEFAULT_LEAD_MINUTES,
  saving: false,
  saveResult: null as 'saved' | 'failed' | null,
}

// Log the unavailable preference once per page load, not once per screen that asks for it.
let warned = false
// The newest save. A slower, older save must not write its result over a newer one.
let saveSeq = 0
// Moves on sign-out. A read that started for the previous user must not write its result.
let generation = 0
// The read that is out, so two screens that ask at once send one request.
let inflight: Promise<void> | null = null

const useDeadlineNotify = create<DeadlineNotifyState>()((set, get) => ({
  ...state,
  get: async options => {
    const { availability } = get()
    if (inflight) return inflight
    if (!options?.force && availability !== 'unknown') return
    // ⚠ A forced read of a preference that is already available keeps it `available` while the
    // read is out, so the settings block does not disappear and come back on each visit.
    if (availability !== 'available') set({ availability: 'loading' })
    const gen = generation

    const read = async () => {
      const userId = getUserId()
      let query = supabase.from('profiles').select('notify_compute_deadline, compute_deadline_lead_minutes')
      if (userId) query = query.eq('id', userId)
      const { data, error } = await query.single()
      if (gen !== generation) return

      if (error) {
        if (!warned) {
          warned = true
          console.warn('Compute time limit email preference is not available:', error.message)
        }
        return set({ availability: error.code === UNDEFINED_COLUMN ? 'unavailable' : 'error' })
      }
      // ⚠ A save that is out owns the values on screen. Its own result decides them.
      if (get().saving) return set({ availability: 'available' })
      set({ availability: 'available', ...parseDeadlinePreference(data) })
    }
    const pending: Promise<void> = read()
      .catch((e: unknown) => {
        if (gen !== generation) return
        console.warn('Compute time limit email preference read failed:', e)
        set({ availability: 'error' })
      })
      .finally(() => {
        if (inflight === pending) inflight = null
      })
    inflight = pending
    return pending
  },

  save: async updates => {
    const userId = getUserId()
    const patch: TablesUpdate<'profiles'> = {}
    if (updates.enabled !== undefined) patch.notify_compute_deadline = updates.enabled
    if (updates.leadMinutes !== undefined) {
      // ⚠ The database refuses a value outside 15..1440. Do not send one.
      if (!isValidLeadMinutes(updates.leadMinutes)) {
        set({ saveResult: 'failed' })
        return false
      }
      patch.compute_deadline_lead_minutes = updates.leadMinutes
    }
    if (Object.keys(patch).length === 0) return false
    if (!userId) {
      set({ saveResult: 'failed' })
      return false
    }

    const seq = ++saveSeq
    const previous = { enabled: get().enabled, leadMinutes: get().leadMinutes }
    // Show the change at once; put it back if the write fails.
    set({ ...updates, saving: true, saveResult: null })

    // ⚠⚠ `.select()` IS THE POINT, NOT A CONVENIENCE. Without it PostgREST answers 204 with no
    // error whether it wrote ONE row or NONE, and a denied or non-matching UPDATE is a zero-row
    // success. `getUserId()` reads a cookie while the RLS policy matches `request_user_id()` from
    // the JWT, so any divergence — a cookie left by an earlier sign-in in the same tab, an
    // identity switch — makes the filter or the policy match nothing: the page says "Saved.", the
    // switch stays on, no mail is ever sent, and a reload shows it off again. A returned row is
    // the only proof the write landed, and it carries the stored values back.
    const { data, error } = await supabase
      .from('profiles')
      .update(patch)
      .eq('id', userId)
      .select('notify_compute_deadline, compute_deadline_lead_minutes')

    const wrote = !error && Array.isArray(data) && data.length > 0
    if (seq !== saveSeq) return wrote
    if (!wrote) {
      console.warn(
        'Compute time limit email preference save failed:',
        error?.message ?? 'the update matched no row'
      )
      set({ ...previous, saving: false, saveResult: 'failed' })
      return false
    }
    set({ saving: false, saveResult: 'saved' })
    return true
  },

  reset: () => {
    saveSeq++
    generation++
    inflight = null
    set({ ...state })
  },
}))

/**
 * The lead time for the in-app deadline notice, in minutes. Reads the preference once.
 *
 * ⚠ The saved lead time, whether the email is on or off. It is 60 minutes while the preference is
 * not read and when it is unavailable. The notice never waits for the read.
 */
export const useNoticeLeadMinutes = (): number => {
  const availability = useDeadlineNotify(s => s.availability)
  const enabled = useDeadlineNotify(s => s.enabled)
  const leadMinutes = useDeadlineNotify(s => s.leadMinutes)
  useEffect(() => {
    void useDeadlineNotify.getState().get()
  }, [])
  return noticeLeadMinutes(availability === 'available', { enabled, leadMinutes })
}

export default useDeadlineNotify
